'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { EntityType } from './lib/tiptap/extensions/EntityReference';
import type { PendingAttachmentDraft } from './types/attachment';
import type { PromptPreset, CreatePromptPresetInput, UpdatePromptPresetInput } from './lib/api';

export type SearchProvider = 'github' | 'gitlab' | 'bitbucket' | 'linear' | 'sentry';
export type EntityResult = {
  entityType: EntityType;
  id: string;
  title: string;
  badge: string;
  htmlUrl?: string;
  path?: string;
  // 목록에는 title을 보여주되, 칩/Markdown에는 이 값을 라벨로 삽입한다(없으면 title 사용).
  // 브랜치처럼 삽입 후에도 레포 구분이 필요할 때 "repo/branch" 형태를 보존하는 용도.
  insertLabel?: string;
};
type Mutation<Input, Output = unknown> = {
  mutateAsync: (input: Input) => Promise<Output>;
  isPending: boolean;
};

/** 앱의 세션·IO·이동만 공급한다. UI와 편집 상태는 패키지가 소유한다. */
export type RunnerRequestUiRuntime = {
  useWorkspaceScope: () => { token: string | null; projectId: string };
  useIsDesktopApp: () => boolean;
  useUploadDraftAttachment: (token: string | null) => Mutation<{ file: File }, PendingAttachmentDraft>;
  validateAttachmentFile: (file: File) => void;
  usePromptPresets: (
    token: string | null,
    projectId: string,
  ) => {
    data?: PromptPreset[];
    error: unknown;
    isError: boolean;
  };
  useCreatePromptPresetMutation: (token: string | null, projectId: string) => Mutation<CreatePromptPresetInput>;
  useUpdatePromptPresetMutation: (
    token: string | null,
    projectId: string,
  ) => Mutation<{ id: string; input: UpdatePromptPresetInput }>;
  useDeletePromptPresetMutation: (token: string | null, projectId: string) => Mutation<string>;
  searchCommandEntities: (
    token: string,
    projectId: string,
    commandId: string,
    query: string,
    translate?: (key: string) => string,
    options?: { signal?: AbortSignal },
  ) => Promise<EntityResult[]>;
  prefetchAvailableProviders: (token: string, projectId: string) => void;
  getCachedAvailableProviders: (projectId: string) => SearchProvider[] | null;
  cacheAvailableProviders: (projectId: string, providers: SearchProvider[]) => void;
  searchProjectEntities: (
    token: string,
    projectId: string,
    params: { q: string },
  ) => Promise<{ meta: { availableProviders: SearchProvider[] } }>;
  fetchEntityTitle: (token: string, projectId: string, entityType: EntityType, entityId: string) => Promise<string>;
  buildEntityGoHref: (entityType: string, entityId: string) => string | null;
};

const RuntimeContext = createContext<RunnerRequestUiRuntime | null>(null);

export const RunnerRequestUiProvider = ({
  runtime,
  children,
}: {
  runtime: RunnerRequestUiRuntime;
  children: ReactNode;
}) => <RuntimeContext.Provider value={runtime}>{children}</RuntimeContext.Provider>;

export const useRunnerRequestUiRuntime = (): RunnerRequestUiRuntime => {
  const runtime = useContext(RuntimeContext);
  if (!runtime) throw new Error('RunnerRequestUiProvider is required');
  return runtime;
};
